import { useMemo } from 'react';
import _ from 'lodash';

import useHealthPlanSpendTrackerStore from '@/modules/deductibles/HealthPlanDetails/stores/useHealthPlanSpendTracker.store.ts';
import useIsEmbeddedOnly from '@/modules/deductibles/hooks/useIsEmbeddedOnly.ts';
import { useServiceConfigs } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/queries';
import { useHealthPlanByIdQuery } from '@/modules/HealthPlan/HealthPlanSetup/queries';
import { useGetProcessingRuleByIdQuery } from '@/modules/ProcessingDefinition/ProcessingRule/queries';

const useTiersModule = () => {
  const coverageLevel = useHealthPlanSpendTrackerStore((state) => state.state.coverageLevel);
  const healthPlanId = useHealthPlanSpendTrackerStore((state) => state.state.healthPlanId);
  const { data: healthPlan, isLoading: isHealthPlanLoading } = useHealthPlanByIdQuery(healthPlanId);
  const { data: serviceConfigs, isLoading: isServiceLoading } = useServiceConfigs(healthPlanId);

  const planStatus = useMemo(() => healthPlan?.plan_status, [healthPlan]);
  const processingRuleId = useMemo(
    () => _.toString(healthPlan?.enrollment_matrix?.processing_rule_id),
    [healthPlan]
  );
  const healthPlanDeductibleConfig = useMemo(
    () => healthPlan?.health_plan_deductible_configs?.find((el) => el.code?.code === coverageLevel),
    [coverageLevel, healthPlan?.health_plan_deductible_configs]
  );
  const { rule, isLoading } = useGetProcessingRuleByIdQuery(processingRuleId);

  const triggerAmounts = useMemo(() => {
    const amounts: Record<string, number> = {};
    healthPlanDeductibleConfig?.trigger_configs?.forEach((tc) => {
      if (tc.trigger_id != null && tc.threshold_amount != null) {
        amounts[`${tc.trigger_id}`] = tc.threshold_amount;
      }
    });
    return amounts;
  }, [healthPlanDeductibleConfig]);
  const isLoadingAll = useMemo(
    () => isHealthPlanLoading || isServiceLoading || isLoading,
    [isHealthPlanLoading, isServiceLoading, isLoading]
  );

  const isEmbeddedOnly = useIsEmbeddedOnly((rule?.tiers ?? []).map((tier) => tier.tierTriggers || []));

  return {
    isLoadingAll,
    planStatus,
    serviceConfigs,
    rule,
    triggerAmounts,
    isEmbeddedOnly,
  };
};

export default useTiersModule;
